import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  clone,
  get,
  groupBy,
  isEmpty,
  sortBy,
} from 'lodash';
import {
  API,
  TableCreatives,
  color,
  GlobalActions,
  DisplayActions,
  ToastActions,
} from 'vibeguide';
import MediaSidebar from './Sidebar/MediaSidebar';
import './OrderCreatives.scss';

function OrderCreatives({
  orderId = '',
  segmentId = '',
  segment = {},
  allowCreativeManagement = false,
  setPanel,
  queueToast,
  setDisplay,
  onUpdateDayParts = () => {},
}) {
  const [creatives, setCreatives] = useState([]);
  const [creativeTableHeight, setCreativeTableHeight] = useState(300);
  const [onSuccess, setOnSuccess] = useState(false);

  const creativeSetup = (dayParts) => {
    let mappedCreativesArr = [];
    const defaultCreative = {
      dayPart: '',
      url: null,
      creativeId: '',
      name: '',
      weight: '',
      fileType: '',
      fileSize: '',
      graphicSize: '',
      durationSeconds: '',
      active: true,
    };

    dayParts.forEach((dayPart) => {
      const {
        startTime, endTime, dateRanges,
      } = dayPart;
      const dayPartString = `${startTime}-${endTime}`;
      let mappedDateRanges = [];
      const newCreatives = [];

      if (dateRanges) {
        mappedDateRanges = dateRanges.map((daterange) => {
          if (!isEmpty(daterange.creatives)) {
            return {
              ...daterange,
              creatives: daterange.creatives.map((creative) => {
                return {
                  ...creative,
                  _id: creative.creativeId,
                  startDate: moment(daterange.startDate).format('ddd, MMM Do, YYYY'),
                  endDate: moment(daterange.endDate).format('ddd, MMM Do, YYYY'),
                  dayPart: dayPartString,
                  fileType: creative.contentType || '',
                  fileSize: creative.fileSizeBytes || '',
                  graphicSize: creative.graphicSize || '',
                  active: true,
                };
              }),
            };
          }

          return daterange;
        });
      }

      mappedDateRanges.forEach((mdr) => {
        if (!isEmpty(mdr.creatives)) {
          // if there are creatives for a date range, we want to show those creatives in the table
          mdr.creatives.forEach((creative) => {
            newCreatives.push(creative);
          });
        } else {
          // if there are no creatives for a date range, we still want to show the range to show it exists
          // and creatives can be added to it
          newCreatives.push({
            ...defaultCreative,
            dayPart: dayPartString,
            startDate: moment(mdr.startDate).format('ddd, MMM Do, YYYY'),
            endDate: moment(mdr.endDate).format('ddd, MMM Do, YYYY'),
          });
        }
      });

      mappedCreativesArr = [
        ...mappedCreativesArr,
        ...newCreatives,
      ];

      if (!newCreatives || newCreatives.length === 0) {
        mappedCreativesArr.push({
          ...defaultCreative,
          dayPart: dayPartString,
        });
      }
    });

    setCreatives(mappedCreativesArr);
  };

  const updateDayParts = async (dayParts) => {
    const response = await API.Order.Segment.updateDayParts({
      _id: orderId,
      segmentId,
      dayParts,
    });

    const successText = 'ORDERSEGMENT.CREATIVES_UPDATED';
    const success = get(response, '[0].type') === successText;

    if (success) {
      queueToast({
        type: 'success',
        title: 'Saved...',
        allowClose: true,
      });

      setPanel({
        show: false,
        children: null,
      });

      setOnSuccess(true);
      creativeSetup(dayParts);
      onUpdateDayParts();
    }

    setOnSuccess(false);
  };

  const creativesToDayParts = (creatives) => {
    const { dayParts } = segment;

    const filteredCreatives = creatives.filter(creative => creative.creativeId !== '');

    // Group by daypart
    const creativesByDayPart = groupBy(filteredCreatives, 'dayPart');

    return dayParts.map(dayPart => {
      const { startTime, endTime } = dayPart;
      const dayPartString = `${startTime}-${endTime}`;
      const dayPartCreatives = creativesByDayPart[dayPartString] || [];

      const updatedCreatives = dayPartCreatives.map(creative => ({
        ...creative,
        weight: creative.weight,
      }));

      return {
        ...dayPart,
        creatives: updatedCreatives,
      };
    });
  };

  const showDisplayPlayer = async (e, messages) => {
    e.preventDefault();
    e.stopPropagation();

    setDisplay({
      show: true,
      messages,
      isLoop: false,
    });
  };

  const onSelectMedia = async (dayPart, messages) => {
    const allCreativesByDayPart = [
      ...creatives.filter(creative => creative.dayPart === dayPart && creative.creativeId !== ''),
      ...messages,
    ];

    const newCreatives = allCreativesByDayPart.map(creative => {
      const {
        active,
        name,
        durationSeconds,
        url,
        creativeId,
        _id,
        weight,
        contentType,
        fileSizeBytes,
        grpahicSize,
      } = creative;

      return {
        creativeId: !creativeId ? _id : creativeId,
        weight: !weight ? '' : weight,
        fileType: contentType || '',
        fileSize: fileSizeBytes || '',
        graphicSize: grpahicSize || '',
        dayPart,
        active,
        name,
        durationSeconds,
        url,
      };
    });

    setCreatives([
      ...creatives.filter(creative => creative.dayPart !== dayPart),
      ...newCreatives,
    ]);

    setPanel({
      show: false,
    });
  };

  const onCloseSidebar = () => {
    setPanel({
      show: false,
      children: null,
    });
  };

  /**
   * When table data row height is fetched or changed
   */
  const onFetchComplete = ({
    rows = [],
    // filters,
    // totalItems,
  }) => {
    // total height of the table (before rows are rendered is 154px)
    const rowCount = rows.length;

    // set the table height equal to the number of rows (for a compact density)
    setCreativeTableHeight((rowCount * 44) + 154 + 1); // 44px per row + 154px for header
  };

  const addCreative = (row) => {
    const { dayParts } = segment;
    const dayPartIndex = dayParts.findIndex(daypart => row.dayPart === `${daypart.startTime}-${daypart.endTime}`);
    const dayPart = dayParts.filter(daypart => row.dayPart === `${daypart.startTime}-${daypart.endTime}`);
    const mediaDayPart = clone(dayPart[0]);

    setPanel({
      show: true,
      backdrop: true,
      width: 400,
      children: (
        <MediaSidebar
          editMedia
          dayPart={mediaDayPart}
          dayPartIndex={dayPartIndex}
          dayParts={dayParts}
          segment={segment}
          onSelectMedia={onSelectMedia}
          saveDayParts={updateDayParts}
          creativeSetup={creativeSetup}
          onCloseSidebar={onCloseSidebar}
        />
      ),
      onClose: onCloseSidebar,
    });
  };

  useEffect(() => {
    if (!segment && !segment.dayParts) return;

    creativeSetup(segment.dayParts);
  }, [segment.dayParts]);

  return (
    <div className="OrderCreatives">
      <div className="creative-title">
        Attached Creatives
      </div>

      <div className="creative-sub-title">
        Media that has been attached to this segment.
      </div>

      <TableCreatives
        className="custom-creative-table"
        columns={allowCreativeManagement
          ? [
            'Day Part',
            'Start Date',
            'End Date',
            'Message Name',
            'Weight',
            'File Type',
            'File Size',
            'Duration',
            '...']
          : [
            'Day Part',
            'Creative Management Unavailable',
          ]}
        collection={sortBy(creatives, ({ dayPart }) => {
          const [startTime] = dayPart.split('-');
          return startTime;
        })}
        style={{
          height: creativeTableHeight,
          border: `1px solid ${color.manatee16}`,
          borderTop: 'none',
          boxSizing: 'border-box',
        }}
        defaultSortBy={{
          label: 'Start Date',
          attr: '',
          direction: 'asc',
        }}
        paginator
        paginatorProps={{
          label: 'Media',
          urlPaging: false,
          urlFilters: true,
        }}
        onAddMedia={addCreative}
        onSuccess={onSuccess}
        editMode={allowCreativeManagement
          && segment.orderStatus !== 'completed'
          && segment.orderStatus !== 'cancelled'}
        showDisplayPlayer={showDisplayPlayer}
        onFetchComplete={onFetchComplete}
      />
    </div>
  );
}

OrderCreatives.propTypes = {
  orderId: PropTypes.string,
  segmentId: PropTypes.string,
  segment: PropTypes.object,
  allowCreativeManagement: PropTypes.bool,
};

const mapDispatchToProps = {
  setPanel: GlobalActions.setPanel,
  setDisplay: DisplayActions.setDisplay,
  queueToast: ToastActions.queueToast,
};

export default connect(null, mapDispatchToProps)(OrderCreatives);
